import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { listaTomos, rutaPortadaTomo, tomosGuardados } from '../api/tomos'
import { useImagen, useVisible } from '../lib/imagenes'
import { leerPortada } from '../lib/portadas'

interface Props {
  ruta: string | null
  titulo: string
  sinLeer?: number
  /** Si se pasa, se usa la portada puesta a mano cuando exista una. */
  mangaId?: number
}

export default function Portada({ ruta, titulo, sinLeer, mangaId }: Props) {
  const { ref, visible } = useVisible<HTMLDivElement>()

  const { data: propia } = useQuery({
    queryKey: ['portadaPropia', mangaId],
    queryFn: () => leerPortada(mangaId!),
    enabled: mangaId !== undefined,
    staleTime: Infinity,
  })

  /*
   * Las portadas de tomo, todas de una vez.
   *
   * La rejilla de la biblioteca pinta una portada por serie y todas preguntan
   * a la vez; al ser la misma consulta, React Query hace **una sola peticion**
   * y se la reparte a todas. Arranca con lo guardado del aparato para que la
   * biblioteca no abra con la portada vieja y la cambie medio segundo despues.
   */
  const { data: tomos, isLoading: buscandoTomos } = useQuery({
    queryKey: ['tomos'],
    queryFn: listaTomos,
    initialData: tomosGuardados,
    // Lo guardado se pinta ya, pero cuenta como viejo: sin esto React Query
    // lo daria por recien traido y no preguntaria al servidor hasta dentro de
    // diez minutos, asi que la primera vez —cache vacio— la biblioteca se
    // quedaba sin portadas de tomo hasta recargar.
    initialDataUpdatedAt: 0,
    staleTime: 10 * 60 * 1000,
  })

  const tomo = mangaId === undefined ? undefined : tomos?.[mangaId]
  const pedida =
    mangaId !== undefined && tomo?.estado === 'lista' && tomo.volumen
      ? rutaPortadaTomo(mangaId, tomo.volumen)
      : null

  /*
   * Si la portada del tomo no llega se vuelve a la de la fuente. Pasa si el
   * servicio dice tenerla pero el fichero no esta —se borro el almacen del
   * VPS, o se apago a la vez desde otro aparato—, y sin esto la serie se
   * quedaria con el hueco y el boton de reintentar en vez de su portada.
   */
  const [falloTomo, setFalloTomo] = useState<string | null>(null)
  const rutaTomo = pedida && pedida !== falloTomo ? pedida : null

  // Mientras no se sepa si hay una propia o una de tomo no se pide la del
  // servidor, para no gastar una descarga que se va a descartar. Sin mangaId
  // no hay nada que esperar y se pide directamente.
  const buscando = mangaId !== undefined && (propia === undefined || buscandoTomos)
  const { url, estado, reintentar } = useImagen(rutaTomo ?? ruta, {
    activo: visible && !buscando && !propia,
  })

  useEffect(() => {
    if (estado === 'fallo' && rutaTomo) setFalloTomo(rutaTomo)
  }, [estado, rutaTomo])

  const [urlPropia, setUrlPropia] = useState<string | null>(null)
  useEffect(() => {
    if (!propia) {
      setUrlPropia(null)
      return
    }
    const objeto = URL.createObjectURL(propia)
    setUrlPropia(objeto)
    return () => URL.revokeObjectURL(objeto)
  }, [propia])

  const mostrada = urlPropia ?? url

  return (
    <div className="portada" ref={ref}>
      {mostrada && <img src={mostrada} alt={titulo} />}

      {estado === 'fallo' && !urlPropia && (
        <button
          className="portada-fallo"
          title="Reintentar"
          onClick={(e) => {
            e.preventDefault()
            reintentar()
          }}
        >
          ↻
        </button>
      )}

      {sinLeer ? <span className="insignia">{sinLeer}</span> : null}
    </div>
  )
}
